import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { DynamicFieldsAPI, FetchAPI, FormAPI, Metadata, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { FragmentNames } from '../../codegen';
import { AccreditationComplementaryDataAliases as Aliases } from '../../codegen/constants/aliases/accreditation-complementary-data-aliases';
import BaseController from '../BaseController';

import { AccreditationSteps } from './accreditation-update-controller';

interface DynamicFieldsData {
  id: string | number;
  title: string;
}

@Controller({ fragment: FragmentNames.ACCREDITATION_COMPLEMENTARY_DATA })
class AccreditationComplementaryDataController extends BaseController {
  @Inject(Aliases.FORM_QUESTIONNAIRE)
  private formQuestionnaire: FormAPI;
  @Inject(Aliases.DYNAMIC_FIELDS_QUESTIONNAIRE)
  private dynamicFields: DynamicFieldsAPI;
  @Inject(Aliases.FETCH_QUESTIONNAIRE)
  private fetchQuestionnaire: FetchAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;

  @OnMount(Aliases.FORM_QUESTIONNAIRE)
  OnMountForm(event: MountEvent): void {
    const api = event.target as FormAPI;
    FormViewService.shouldEditForm(api, AccreditationSteps.QUESTIONNAIRE);
  }

  @OnSubmit(Aliases.FORM_QUESTIONNAIRE)
  onSubmitForm(event: FormAPI): void {
    const { record } = event;
    const { nrSeqAccreditation, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const dynamicFieldsData = this.dynamicFields.getMetadata() as DynamicFieldsData;
    const body = {
      nrSeqTypeAccreditation,
      wModeloSubmitDTO: {
        formSubmit: record,
        dynamicFieldId: dynamicFieldsData.id,
      },
    };
    HttpService.post(Endpoint.POST.QUESTIONNAIRE_METADATA, { nrSeqProvider, nrSeqAccreditation }, { body })
      .then(() => {
        ToastService.open({ message: MessageService.translate('provider.questionnaire-answered'), level: ToastLevel.SUCCESS });
      })
      .catch(() => {
        ToastService.open({ message: MessageService.translate('provider.general-error'), level: ToastLevel.WARNING });
      });
  }

  @OnMount(Aliases.DYNAMIC_FIELDS_QUESTIONNAIRE)
  public async onMount(): Promise<void> {
    try {
      const { nrSeqAccreditation, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
      const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
      const response = await HttpService.get(Endpoint.GET.QUESTIONNAIRE_METADATA, { nrSeqProvider, nrSeqTypeAccreditation });
      catchRenderException(() => {
        this.dynamicFields.setMetadata(response.body as Metadata);
        this.formQuestionnaire.setTitle((response.body as DynamicFieldsData).title);
        this.context.set({ nrSeqProvider, nrSeqAccreditation });
        this.fetchQuestionnaire.setEndpoint(Endpoint.GET.QUESTIONNAIRE_REPLEIS);
        this.fetchQuestionnaire.setParameters({ nrSeqProvider, nrSeqAccreditation });
      });
    } catch (error) {
      catchRenderException(() => this.formQuestionnaire.setDisabled(true));
      ToastService.open({ message: MessageService.translate('provider.general-error'), level: ToastLevel.WARNING });
    }
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.QUESTIONNAIRE);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }
}

export {
  AccreditationComplementaryDataController,
};
